import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, TextField,
  Typography,
} from '@mui/material'
import AddIcon          from '@mui/icons-material/Add'
import EditIcon         from '@mui/icons-material/EditOutlined'
import DeleteIcon       from '@mui/icons-material/DeleteOutlined'
import VideocamIcon     from '@mui/icons-material/Videocam'
import StarIcon         from '@mui/icons-material/Star'
import EventRepeatIcon  from '@mui/icons-material/EventRepeat'
import { supabase } from '../../supabaseClient'

interface MentorOption {
  user_id:    string
  first_name: string
  last_name:  string
  role:       string   // 'lead' = primary
}

interface OfficeHourRow {
  office_hour_id: string
  mentor_id:      string
  weekday:        number
  start_time:     string   // 'HH:MM[:SS]'
  end_time:       string
  zoom_id:        string | null
  zoom_url:       string | null
  zoom_passcode:  string | null
  session_label:  string | null
  session_start:  string | null
  session_end:    string | null
  notes:          string | null
}

interface Props {
  /** Class mentors (primary + helpers) whose office hours are shown. */
  mentors:  MentorOption[]
  /** When true, render management controls (admin / office_manager). */
  editable: boolean
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function fmtTime(t: string): string {
  const [hStr, mStr] = t.split(':')
  let h = Number(hStr)
  const m = Number(mStr)
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return m === 0 ? `${h}:00 ${ampm}` : `${h}:${String(m).padStart(2, '0')} ${ampm}`
}

function fmtDate(s: string): string {
  const [y, mo, d] = s.split('-').map(Number)
  return new Date(y, mo - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

interface FormState {
  office_hour_id?: string
  mentor_id:     string
  weekday:       number
  start_time:    string
  end_time:      string
  zoom_id:       string
  zoom_url:      string
  zoom_passcode: string
  session_label: string
  session_start: string
  session_end:   string
  notes:         string
}

const EMPTY_FORM: FormState = {
  mentor_id: '', weekday: 3, start_time: '19:30', end_time: '20:00',
  zoom_id: '', zoom_url: '', zoom_passcode: '', session_label: '',
  session_start: '', session_end: '', notes: '',
}

export default function ClassOfficeHoursSection({ mentors, editable }: Props) {
  const [rows,       setRows]       = useState<OfficeHourRow[]>([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form,       setForm]       = useState<FormState>(EMPTY_FORM)
  const [saving,     setSaving]     = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<OfficeHourRow | null>(null)
  const [deleting,   setDeleting]   = useState(false)

  const mentorIds = mentors.map(m => m.user_id)
  const mentorKey = mentorIds.join(',')
  const mentorMap: Record<string, MentorOption> = Object.fromEntries(mentors.map(m => [m.user_id, m]))

  async function load() {
    setLoading(true)
    if (mentorIds.length === 0) { setRows([]); setLoading(false); return }
    const { data, error: loadErr } = await supabase
      .from('mentor_office_hour')
      .select('office_hour_id, mentor_id, weekday, start_time, end_time, zoom_id, zoom_url, zoom_passcode, session_label, session_start, session_end, notes')
      .in('mentor_id', mentorIds)
      .eq('active', true)
    if (loadErr) { setError(loadErr.message); setLoading(false); return }
    // Sort: primary mentor first, then by weekday, then start time.
    const sorted = (data ?? []).slice().sort((a, b) =>
      (mentorMap[a.mentor_id]?.role === 'lead' ? 0 : 1) - (mentorMap[b.mentor_id]?.role === 'lead' ? 0 : 1) ||
      a.weekday - b.weekday ||
      a.start_time.localeCompare(b.start_time)
    )
    setRows(sorted as OfficeHourRow[])
    setLoading(false)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [mentorKey])

  function openCreate() {
    setError(null)
    setForm({ ...EMPTY_FORM, mentor_id: mentors.find(m => m.role === 'lead')?.user_id ?? mentors[0]?.user_id ?? '' })
    setDialogOpen(true)
  }

  function openEdit(r: OfficeHourRow) {
    setError(null)
    setForm({
      office_hour_id: r.office_hour_id,
      mentor_id:     r.mentor_id,
      weekday:       r.weekday,
      start_time:    r.start_time.slice(0, 5),
      end_time:      r.end_time.slice(0, 5),
      zoom_id:       r.zoom_id ?? '',
      zoom_url:      r.zoom_url ?? '',
      zoom_passcode: r.zoom_passcode ?? '',
      session_label: r.session_label ?? '',
      session_start: r.session_start ?? '',
      session_end:   r.session_end ?? '',
      notes:         r.notes ?? '',
    })
    setDialogOpen(true)
  }

  async function handleSave() {
    setError(null)
    if (!form.mentor_id) { setError('Choose a mentor.'); return }
    if (!form.start_time || !form.end_time) { setError('Start and end time are required.'); return }
    if (form.end_time <= form.start_time)   { setError('End time must be after the start time.'); return }
    if (form.session_start && form.session_end && form.session_end < form.session_start) {
      setError('Session end must be on or after the session start.'); return
    }

    setSaving(true)
    const payload = {
      mentor_id:     form.mentor_id,
      weekday:       form.weekday,
      start_time:    form.start_time,
      end_time:      form.end_time,
      zoom_id:       form.zoom_id.trim() || null,
      zoom_url:      form.zoom_url.trim() || null,
      zoom_passcode: form.zoom_passcode.trim() || null,
      session_label: form.session_label.trim() || null,
      session_start: form.session_start || null,
      session_end:   form.session_end || null,
      notes:         form.notes.trim() || null,
      updated_at:    new Date().toISOString(),
    }
    const { error: saveErr } = form.office_hour_id
      ? await supabase.from('mentor_office_hour').update(payload).eq('office_hour_id', form.office_hour_id)
      : await supabase.from('mentor_office_hour').insert(payload)
    setSaving(false)
    if (saveErr) { setError(saveErr.message); return }
    setDialogOpen(false)
    load()
  }

  async function handleDelete(r: OfficeHourRow) {
    setDeleting(true)
    const { error: delErr } = await supabase.from('mentor_office_hour').delete().eq('office_hour_id', r.office_hour_id)
    setDeleting(false)
    setConfirmDelete(null)
    if (delErr) { setError(delErr.message); return }
    load()
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventRepeatIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Office Hours {rows.length > 0 && `(${rows.length})`}
          </Typography>
        </Box>
        {editable && (
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openCreate}>
            Add office hour
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">
              No office hours set for this class's mentors yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {rows.map((r, i) => {
              const m = mentorMap[r.mentor_id]
              const mentorName = m ? `${m.first_name} ${m.last_name}`.trim() : 'Mentor'
              return (
                <Box
                  key={r.office_hour_id}
                  sx={{ p: 2, borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', gap: 2 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{mentorName}</Typography>
                      {m?.role === 'lead' && <Chip label="Primary" color="primary" size="small" icon={<StarIcon />} />}
                    </Box>
                    <Typography variant="body2">
                      {WEEKDAYS[r.weekday]}s · {fmtTime(r.start_time)}–{fmtTime(r.end_time)} PT
                    </Typography>
                    {(r.session_label || r.session_start) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {r.session_label}
                        {r.session_label && (r.session_start || r.session_end) && ' · '}
                        {r.session_start && fmtDate(r.session_start)}
                        {r.session_start && r.session_end && ' – '}
                        {r.session_end && fmtDate(r.session_end)}
                      </Typography>
                    )}
                    {(r.zoom_id || r.zoom_passcode) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <VideocamIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          Zoom{r.zoom_id && ` · ID ${r.zoom_id}`}{r.zoom_passcode && ` · Passcode ${r.zoom_passcode}`}
                        </Typography>
                      </Box>
                    )}
                    {r.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        {r.notes}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {r.zoom_url && (
                      <Button size="small" variant="contained" href={r.zoom_url} target="_blank" rel="noopener noreferrer">
                        Join
                      </Button>
                    )}
                    {editable && (
                      <>
                        <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => setConfirmDelete(r)}><DeleteIcon fontSize="small" /></IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </Paper>

      {/* ── Add / edit dialog ── */}
      <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{form.office_hour_id ? 'Edit Office Hour' : 'Add Office Hour'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            This is the mentor's standing weekly office hour, shared across every class they mentor.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <FormControl size="small" fullWidth required>
              <InputLabel>Mentor</InputLabel>
              <Select label="Mentor" value={form.mentor_id} onChange={e => setForm(f => ({ ...f, mentor_id: e.target.value }))}>
                {mentors.map(m => (
                  <MenuItem key={m.user_id} value={m.user_id}>
                    {`${m.first_name} ${m.last_name}`.trim()}{m.role === 'lead' ? ' (Primary)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }} required>
                <InputLabel>Weekday</InputLabel>
                <Select label="Weekday" value={form.weekday} onChange={e => setForm(f => ({ ...f, weekday: Number(e.target.value) }))}>
                  {WEEKDAYS.map((d, i) => <MenuItem key={i} value={i}>{d}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                label="Start (PT)" size="small" type="time" required
                value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }} sx={{ flex: 1 }}
              />
              <TextField
                label="End (PT)" size="small" type="time" required
                value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }} sx={{ flex: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Zoom ID" size="small" value={form.zoom_id}
                onChange={e => setForm(f => ({ ...f, zoom_id: e.target.value }))} sx={{ flex: 1 }}
              />
              <TextField
                label="Passcode" size="small" value={form.zoom_passcode}
                onChange={e => setForm(f => ({ ...f, zoom_passcode: e.target.value }))} sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Zoom join link" size="small" type="url" fullWidth
              placeholder="https://us06web.zoom.us/j/…"
              value={form.zoom_url} onChange={e => setForm(f => ({ ...f, zoom_url: e.target.value }))}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Session label" size="small" placeholder="Summer 2026"
                value={form.session_label} onChange={e => setForm(f => ({ ...f, session_label: e.target.value }))}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Session start" size="small" type="date"
                value={form.session_start} onChange={e => setForm(f => ({ ...f, session_start: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }} sx={{ flex: 1 }}
              />
              <TextField
                label="Session end" size="small" type="date"
                value={form.session_end} onChange={e => setForm(f => ({ ...f, session_end: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }} sx={{ flex: 1 }}
              />
            </Box>

            <TextField
              label="Notes (optional)" size="small" fullWidth multiline rows={2}
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete office hour?</DialogTitle>
        <DialogContent>
          <Typography>
            This removes the mentor's standing office hour from every class they mentor.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" disabled={deleting} onClick={() => confirmDelete && handleDelete(confirmDelete)}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
